import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface FormShellProps {
  onSubmit: (e: React.FormEvent) => void;
  submitting: boolean;
  submitLabel: string;
  /** Cancel button action; defaults to going back */
  onCancel?: () => void;
  /** [anchor id, label] of each card, for the "on this page" list */
  sections: [string, string][];
  /** Extra cards at the top of the side column */
  side?: ReactNode;
  children: ReactNode;
}

/** Single-page form layout: stacked cards on the left, sticky side column with actions and summary. */
export default function FormShell({ onSubmit, submitting, submitLabel, onCancel, sections, side, children }: FormShellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Opened in a new tab: no history to go back to, so fall back to the admin.
  const back = () => location.key === 'default' ? navigate('/admin') : navigate(-1);

  return (
    <form onSubmit={onSubmit} className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] items-start">
      <div className="space-y-6 min-w-0">{children}</div>
      <aside className="space-y-4 lg:sticky lg:top-24">
        {side}
        <div className="flex flex-col gap-2">
          <button type="submit" disabled={submitting}
            className="h-11 rounded-full bg-primary text-white font-semibold text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {submitting && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
            {submitLabel}
          </button>
          <button type="button" onClick={onCancel ?? back} disabled={submitting}
            className="h-11 rounded-full border border-line text-sm font-medium text-ink hover:bg-surface transition-colors disabled:opacity-50">
            {t('cancel')}
          </button>
        </div>
        <nav aria-label={t('on_this_page') as string} className="hidden lg:block rounded-2xl border border-line p-5">
          <p className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-2">{t('on_this_page')}</p>
          {sections.map(([id, label]) => (
            <a key={id} href={`#${id}`} className="block py-1.5 text-sm text-muted hover:text-ink transition-colors">{label}</a>
          ))}
        </nav>
      </aside>
    </form>
  );
}
